import { skipToken } from '@tanstack/react-query';
import { useState } from 'react';
import { ModalDialog } from '../../../components/modalDialog.tsx';
import { ProtocolSelectDialog } from '../../../components/protocolSelectDialog.tsx';
import { Measurement, Record } from '../../../types/sharedTypeImpl.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { ProtocolTable } from './protocolTable.tsx';
import { useCreateMeasurement } from './useCreateMeasurement.ts';
import { useMeasurementOptions } from './useMeasurementOptions.ts';


interface Props {
    equipment: string;
    equipmentDisplayName: string;
    product: string | null;
    partCount: number | null;
    targetQuantity: number | null;
    inOfflineMode: boolean;
    protocolId: string | null;
    onMeasurementAdded: (measurement: Measurement, record: Record | null) => void;
    onClose: () => void;
}

export function MeasurementDialog({
    equipment,
    equipmentDisplayName,
    product,
    partCount,
    targetQuantity,
    inOfflineMode,
    protocolId,
    onMeasurementAdded,
    onClose,
}: Props) {
    const [showNewProductDialog, setShowNewProductDialog] = useState(false);
    const isBatchComplete = partCount != null && targetQuantity != null && partCount > targetQuantity;

    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery(equipment ? { type: 'cmm/vmm' } : skipToken);

    const measurementOptions = useMeasurementOptions(equipment, product, protocolId, inOfflineMode);

    const { isExcelPending, pendingProtocolId, handleExcelSelected, handleProtocolSelected, handleVariantSelected } = useCreateMeasurement(
        equipment,
        product,
        onMeasurementAdded,
    );

    return (
        <ModalDialog isOpen={true} cancelOnOutsideClick={true} onCancel={onClose}>
            <div className='flex--horz' style={{ gap: '24px' }}>
                <p>
                    {equipmentDisplayName} - {product ?? 'Product unknown'}
                </p>

                {isBatchComplete && (
                    <p style={{ color: '#b30000', fontWeight: 700 }}>The production batch is complete, switch to a new work order.</p>
                )}

                {inOfflineMode && (
                    <button className='button button--primary' onClick={() => setShowNewProductDialog(true)} children='Select another product' />
                )}
            </div>

            <ProtocolTable
                options={measurementOptions}
                cmmEquipments={cmmEquipments}
                isExcelPending={isExcelPending}
                pendingProtocolId={pendingProtocolId}
                onExcelSelect={handleExcelSelected}
                onProtocolSelect={handleProtocolSelected}
                onVariantSelect={handleVariantSelected}
            />

            {showNewProductDialog && (
                <ProtocolSelectDialog
                    isOpen={showNewProductDialog}
                    pendingProtocolId={pendingProtocolId}
                    onProtocolSelect={handleProtocolSelected}
                    onClose={() => setShowNewProductDialog(false)}
                />
            )}
        </ModalDialog>
    );
}
